import React, { useEffect, useContext } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import { useRouter, useParams, useSearchParams } from "../../shared/hooks/useNavigation";
import PropTypes from "prop-types";
import { isMobile } from "react-device-detect";
import sanitizeHtml from "sanitize-html";

import CustomPage from "../../components/CustomPage/CustomPage";
import Footer from "../../components/Footer/Footer";
import ComponentContext from "../../contexts/ComponentContext";
import { useStores } from "../../contexts/StoreContext";
import apiInstance from "../../services/api";
import JobStructuredData from "../../shared/components/JobStructuredData/JobStructuredData";
import ApplicationFormModal from "../../shared/components/SearchResults/ApplicationFormModal";
import Seo from "../../shared/components/Seo/Seo";
import getFilters from "../../shared/helpers/getFilters/getFilters";
import getLayoutStaticProps from "../../shared/helpers/getDataHelpers/getLayoutStaticProps";

function JobPage(props) {
  const { vacancy, layoutProps } = props;
  const singleJobPage = layoutProps?.singleJobPage;
  const { rolesStore, configStore, vacancyStore, modalStore, filtersStore } =
    useStores();
  const { formHeaderBackgroundColor } = useContext(ComponentContext);
  const router = useRouter();
  const searchParams = useParams();
  const urlSearchParams = useSearchParams();
  const showApplyForm = urlSearchParams?.get("showApp");
  const { openModal } = modalStore;
  const { setFilters, filtersMapping } = filtersStore;

  const { setSelectedVacancy, selectedVacancy, setIsVacancyLoading } =
    vacancyStore;

  useEffect(() => {
    return () => {
      setSelectedVacancy({});
      setIsVacancyLoading(true);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      const queryFilters = await getFilters(searchParams, router.locale, filtersMapping);
      setFilters(queryFilters);
      setSelectedVacancy(vacancy);
      setIsVacancyLoading(false);
    };
    load();
  }, [vacancy]);

  useEffect(() => {
    if (!isEmpty(selectedVacancy) && showApplyForm) {
      openModal({
        content: (
          <ApplicationFormModal
            formHeaderBackgroundColor={formHeaderBackgroundColor}
          />
        ),
        fullscreen: isMobile,
      });
    }
  }, [selectedVacancy]);

  if (isEmpty(selectedVacancy)) {
    return null;
  }

  // Fallback to empty objects/arrays to prevent SSR crashes
  const roles = rolesStore?.roles || [];
  const config = configStore?.config || {};

  const role = roles?.find(
    (singleRole) => singleRole.roleName === vacancy?.category
  );

  const locationLabel = [vacancy.location?.city].filter(Boolean).join(", ");

  const metaDescription = sanitizeHtml(vacancy?.description || "", {
    allowedTags: [],
  });

  let metaImage = config?.logo;

  if (singleJobPage?.seo?.metaImage) {
    metaImage = singleJobPage?.seo?.metaImage;
  }

  let keywords = `${vacancy?.category || ""}, ${vacancy?.hoursType || ""}, ${
    vacancy?.title || ""
  }, ${vacancy?.location?.city || vacancy?.location?.county || ""}`;

  if (singleJobPage?.seo?.keywords) {
    keywords = singleJobPage.seo.keywords;
  }

  let canonicalUrl = `/job/${vacancy?.id || ""}`;
  if (singleJobPage?.locale) {
    canonicalUrl = `/${singleJobPage.locale}${canonicalUrl}`;
  }

  const seoObject = {
    canonicalUrl,
    keywords,
    metaDescription,
    metaImage,
    noFollow:
      selectedVacancy.status === "CLOSED" || process?.env?.ENV === "dev",
    validThrough:
      selectedVacancy.status === "CLOSED" ? selectedVacancy.startDate : null,
    metaTitle: `${vacancy.title}, ${locationLabel} - ${config.siteName}`,
    ...role?.seo,
  };

  let defaultSingleJobContent;

  if (isEmpty(singleJobPage?.data?.content)) {
    defaultSingleJobContent = {
      content: [{ __component: "reusable-components.single-job", id: 1 }],
    };
  }

  return (
    <>
      <JobStructuredData vacancy={selectedVacancy} />
      <Seo seoAttributes={seoObject} />
      <CustomPage
        pageAttributes={defaultSingleJobContent || singleJobPage.data}
      />
      {singleJobPage?.data?.showFooter && <Footer />}
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps({ params, locale, draftMode = false }) {
  const vId = params.jobSlug[0];
  const additionalSegments = params.jobSlug.slice(1);

  if (additionalSegments.length > 0) {
    return {
      redirect: {
        destination: `/job/${vId}`,
        permanent: true,
      },
    };
  }

  try {
    const vacancy = await apiInstance.getSingleVacancy(
      params.jobSlug[0].split("-")[0]
    );

    const [vacancyId] = params.jobSlug;

    const vacancyToPass = {
      positionProfile: vacancy?.data?.positionProfile,
      ...(vacancy?.data?.positionProfile || null),
      company: vacancy?.data?.company || null,
      companyId: vacancy?.data?.company?.id || null,
      companyName: vacancy?.data?.company?.name || null,
      directApply: vacancy?.data?.directApply || true,
      id: vacancyId || null,
      status: vacancy?.data?.status || null,
      tenant: vacancy?.data?.tenant || null,
    };

    const { layoutProps } = await getLayoutStaticProps(locale || "en-GB", { forceRefresh: draftMode });

    return {
      props: {
        hydrationData: {
          vacancyStore: {
            selectedVacancy: vacancyToPass,
          },
        },
        vacancy: vacancyToPass,
        ...(layoutProps ? { layoutProps } : {}),
      },
    };
  } catch (data) {
    return {
      redirect: {
        destination: "/",
      },
    };
  }
}

export default observer(JobPage);

JobPage.defaultProps = {
  vacancy: {},
};

JobPage.propTypes = {
  vacancy: PropTypes.shape(),
};
